import { apiDelete, apiGet, apiPatch, apiPost } from './client';
import type { Principal } from '@/types/principal';
import type { Paginated } from '@/types/platform';

export interface ListPrincipalsParams {
  page?: number;
  per_page?: number;
  /**
   * Status filter. Omitted = active only (the legacy default used by the
   * Access Simulator and Answer Synthesis pickers). The Directory passes
   * 'all' so deactivated users stay visible.
   */
  status?: 'active' | 'inactive' | 'suspended' | 'all';
  /** Case-insensitive substring filter on display name or email. */
  search?: string;
  /** Exact group name the principal is a member of. */
  group?: string;
}

/** Fetch a paginated page of principals for the current organization. */
export function getPrincipals(
  params: ListPrincipalsParams = {},
): Promise<Paginated<Principal>> {
  return apiGet<Paginated<Principal>>(
    '/principals',
    params as Record<string, string | number | undefined>,
  );
}

export interface CreatePrincipalInput {
  email: string;
  display_name?: string;
  groups?: string[];
  roles?: string[];
  attributes?: Record<string, unknown>;
}

export interface UpdatePrincipalInput {
  display_name?: string;
  groups?: string[];
  roles?: string[];
  attributes?: Record<string, unknown>;
  status?: 'active' | 'inactive' | 'suspended';
}

/** Create a principal in the org's built-in local directory (all plans, per-plan limit). */
export function createPrincipal(input: CreatePrincipalInput): Promise<Principal> {
  return apiPost<Principal>('/principals', input);
}

/** Update a local principal. Synced principals are rejected by the server (422). */
export function updatePrincipal(id: string, input: UpdatePrincipalInput): Promise<Principal> {
  return apiPatch<Principal>(`/principals/${id}`, input);
}

/** Deactivate a local principal (status -> inactive). Never a hard delete. */
export function deactivatePrincipal(id: string): Promise<void> {
  return apiDelete<void>(`/principals/${id}`);
}
